import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from './Button';

export default function UserMenu() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const navItems = [
    { to: '/', label: 'All Games' },
    { to: '/kepithor', label: 'Kepithor Games' },
    { to: '/playable', label: 'Browser Games' },
    { to: 'https://crypto.kepithorstudios.com', label: 'Crypto' }, // external
  ];

  const linkClasses = (active) =>
    [
      'px-3 py-2 rounded-xl border text-sm transition',
      active
        ? 'border-emerald-400 bg-slate-900 text-emerald-200'
        : 'border-slate-700 bg-slate-900/70 text-slate-100 hover:border-emerald-400 hover:text-emerald-200',
    ].join(' ');

  const mobileLinkClasses = (active) =>
    [
      'w-full text-left px-4 py-2 rounded-xl border text-sm transition',
      active
        ? 'border-emerald-400 bg-slate-900 text-emerald-200'
        : 'border-slate-700 bg-slate-900/80 text-slate-100 hover:border-emerald-400 hover:text-emerald-200',
    ].join(' ');

  // helper to render internal/external links
  const renderNavItem = (item, isMobile = false) => {
    const isExternal = item.to.startsWith('http');

    if (isExternal) {
      return (
        <a
          key={item.to}
          href={item.to}
          target="_blank"
          rel="noopener noreferrer"
          className={isMobile ? mobileLinkClasses(false) : linkClasses(false)}
        >
          {item.label}
        </a>
      );
    }

    const isActive =
      item.to === '/'
        ? location.pathname === '/'
        : location.pathname.startsWith(item.to);

    return (
      <Link
        key={item.to}
        to={item.to}
        className={isMobile ? mobileLinkClasses(isActive) : linkClasses(isActive)}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <div className="flex items-center gap-3">
      {/* DESKTOP NAV (md and up) */}
      <nav className="hidden md:flex items-center gap-3">
        {navItems.map((item) => renderNavItem(item))}

        {/* Auth – desktop */}
        {user ? (
          <div className="flex items-center gap-3">
            <p className="bg-slate-900/70 inline-flex items-center rounded-full border border-slate-700 px-4 py-1 text-sm text-slate-100 max-w-[180px] truncate">
              {user.displayname || user.email}
            </p>
            <Button
              onClick={logout}
              className="bg-emerald-500 text-slate-950 hover:bg-emerald-400 text-sm"
            >
              Log out
            </Button>
          </div>
        ) : (
          <>
            <Link
              state={{ from: location }}
              to="/login"
              className="bg-slate-900/70 text-slate-100 px-3 py-2 rounded-2xl border border-slate-700 hover:border-emerald-400 hover:text-emerald-200 transition text-sm"
            >
              Log in
            </Link>
            <Link
              state={{ from: location }}
              to="/register"
              className="bg-emerald-500 text-slate-950 px-3 py-2 rounded-2xl font-semibold hover:bg-emerald-400 transition text-sm"
            >
              Register
            </Link>
          </>
        )}
      </nav>

      {/* MOBILE: animated hamburger button */}
      <div className="ml-auto md:hidden float-right">
        <button
          type="button"
          className="md:hidden flex flex-col items-center justify-center gap-[4px] rounded-lg border border-slate-700 bg-slate-900/80 p-2 text-slate-100 hover:border-emerald-400 hover:text-emerald-200 transition"
          onClick={() => setIsOpen((o) => !o)}
          aria-label="Toggle navigation menu"
          aria-expanded={isOpen}
        >
          <span
            className={[
              'block w-6 h-[2px] bg-current rounded transition-transform duration-200',
              isOpen ? 'translate-y-[6px] rotate-45' : '',
            ].join(' ')}
          />
          <span
            className={[
              'block w-6 h-[2px] bg-current rounded transition-all duration-200',
              isOpen ? 'opacity-0 scale-x-50' : 'opacity-100',
            ].join(' ')}
          />
          <span
            className={[
              'block w-6 h-[2px] bg-current rounded transition-transform duration-200',
              isOpen ? '-translate-y-[6px] -rotate-45' : '',
            ].join(' ')}
          />
        </button>
      </div>

      {/* MOBILE MENU PANEL */}
      <div
        className={[
          'md:hidden fixed inset-x-0 top-16 z-40',
          'w-screen max-w-screen',
          'origin-top transform transition-all duration-300',
          'bg-slate-950 border-t border-slate-800',
          isOpen
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-4 pointer-events-none',
        ].join(' ')}
      >
        <div className="px-4 py-3 flex flex-col gap-2 overflow-x-hidden">
          {/* Primary nav links */}
          {navItems.map((item) => renderNavItem(item, true))}

          {/* Auth – mobile */}
          {user ? (
            <div className="mt-3 flex flex-col gap-2">
              <p className="bg-slate-900/80 inline-flex items-center rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-100">
                {user.displayname || user.email}
              </p>
              <button
                onClick={logout}
                className="w-full bg-emerald-500 text-slate-950 rounded-xl px-4 py-2 text-sm font-semibold hover:bg-emerald-400 transition"
              >
                Log out
              </button>
            </div>
          ) : (
            <div className="mt-3 flex flex-col gap-2">
              <Link
                state={{ from: location }}
                to="/login"
                className="w-full bg-slate-900/80 text-slate-100 px-4 py-2 rounded-xl border border-slate-700 hover:border-emerald-400 hover:text-emerald-200 transition text-sm text-center"
              >
                Log in
              </Link>
              <Link
                state={{ from: location }}
                to="/register"
                className="w-full bg-emerald-500 text-slate-950 px-4 py-2 rounded-xl font-semibold hover:bg-emerald-400 transition text-sm text-center"
              >
                Register
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
